import { createFetch, type UseFetchOptions } from "@vueuse/core";

import useNotFoundRouting from "@/composition/shared/useNotFoundRouting.ts";
import { t } from "@/i18n.ts";
import router from "@/router.ts";
import { useLayoutStore } from "@/stores/layout.ts";
import { auth0Init } from "@/utils/auth.ts";

let token: string | undefined;
export async function getAccessToken() {
  try {
    token = await auth0Init.getAccessTokenSilently();
    return token;
  } catch (e: any) {
    // eslint-disable-next-line no-console -- we want the error to be logged
    console.error(e);

    // Handle common Auth0 session/auth errors
    const errorCode = e?.error || e?.message || "";
    if (
      errorCode.includes("login_required") ||
      errorCode.includes("consent_required") ||
      errorCode.includes("interaction_required")
    ) {
      // Redirect to login/logout to clear session and re-authenticate
      await auth0Init.logout({
        logoutParams: {
          returnTo: window.location.origin,
        },
      });
      return;
    }

    throw new Error("FAILED TO GET TOKEN", { cause: e });
  }
}

async function handleNotFound(status: number) {
  if (status === 404) {
    await useNotFoundRouting(router, router.currentRoute.value);
  }
}

async function handleUnauthorized(status: number) {
  if (status === 401) {
    await auth0Init.logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  }
}

function handleServerError(status: number, error: any) {
  if (status >= 500 && router.currentRoute.value?.name !== "forbidden") {
    const layoutStore = useLayoutStore();
    layoutStore.addSnackbarMessage(
      `${status}: ${error}`,
      "error",
      "alert",
      true,
    );
  }
}

// this is needed to prevent a redirect from feed view if internalRefs return 404 for images
function isContentRequest(url: string): boolean {
  const urnPattern = /\/api\/v\d+\/urn:newsml:[^:]+:\d{8}:[^/?#]+/;
  const dpaevtPattern = /\/api\/v\d+\/dpaevt:[^/?#]+/;
  return urnPattern.test(url) || dpaevtPattern.test(url);
}

/**
 *  We need to handle parse_exception with problematic query extraction - Error: 400 Bad request
 *  Example: "parse_exception: Cannot parse '<>?OR\"': Lexical error at line 1, column 7.  Encountered: <EOF> after : \"\""
 */

function handleParseExceptionMessage(rawMessage: string): string {
  if (!rawMessage) return "An unknown error occurred. Please try again.";

  const parseMatch = rawMessage.match(/Cannot parse '([^']+)'/);
  if (rawMessage.includes("parse_exception")) {
    if (parseMatch) {
      return `${t("errors.400.parseExceptionPrefix")}  "${parseMatch[1]}" ${t("errors.400.parseException")}`;
    }
    return t("errors.400.defaultMessage");
  }
  return t("errors.400.fallbackMessage");
}

const options: UseFetchOptions = {
  async beforeFetch({ options }) {
    const accessToken = await getAccessToken();
    options.headers = {
      ...options.headers,
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    };
    return { options };
  },
  async onFetchError({ response, error, data }) {
    if (response) {
      await handleUnauthorized(response.status);
      // Detect URN-based content requests
      if (
        response.status === 404 ||
        (response.status === 403 && isContentRequest(response.url))
      ) {
        // Do NOT redirect; return null
        return { response, error, data: null };
      }
      if (response.status === 400) {
        const layoutStore = useLayoutStore();
        const userFriendlyMessage = handleParseExceptionMessage(data?.message);
        layoutStore.addSnackbarMessage(
          userFriendlyMessage,
          "error",
          "alert-filled",
        );
        return { response, error, data };
      }
      await handleNotFound(response.status);
      handleServerError(response.status, data?.error || "Unknown Error");
    }
    return { response, error, data };
  },
  updateDataOnError: true,
};

export const bookmarkApiClient = createFetch({
  baseUrl: `${import.meta.env.VITE_BOOKMARK_ROOT_URL}/api/v1`,
  combination: "chain",
  options,
  fetchOptions: {
    mode: "cors",
  },
});
export const contentApiClient = createFetch({
  baseUrl: import.meta.env.VITE_CONTENT_API_BASE,
  combination: "chain",
  options,
  fetchOptions: {
    mode: "cors",
  },
});
export const relatedContentApiClient = createFetch({
  baseUrl: import.meta.env.VITE_RELATED_CONTENT_ROOT_URL,
  combination: "chain",
  options,
  fetchOptions: {
    mode: "cors",
  },
});
export const savedSearchApiClient = createFetch({
  baseUrl: import.meta.env.VITE_SAVEDSEARCH_API_BASE,
  combination: "chain",
  options,
  fetchOptions: {
    mode: "cors",
  },
});
export const cvdApiClient = createFetch({
  baseUrl: `${import.meta.env.VITE_CVD_API_BASE}/api/v1`,
  combination: "chain",
  options,
  fetchOptions: {
    mode: "cors",
  },
});
export const desksApiClient = createFetch({
  baseUrl: import.meta.env.VITE_DESKS_API_BASE,
  combination: "chain",
  options,
  fetchOptions: {
    mode: "cors",
  },
});
export const messengerApiClient = createFetch({
  baseUrl: import.meta.env.VITE_MESSAGES_API_BASE,
  combination: "chain",
  options,
  fetchOptions: {
    mode: "cors",
  },
});
export const commentsApiClient = createFetch({
  baseUrl: import.meta.env.VITE_COMMENTS_API_BASE,
  combination: "chain",
  options,
  fetchOptions: {
    mode: "cors",
  },
});
export const signupApiClient = createFetch({
  baseUrl: import.meta.env.VITE_PRESIGNUP_BASE,
  combination: "chain",
  options,
  fetchOptions: {
    mode: "cors",
  },
});
export const authorizationApiClient = createFetch({
  baseUrl: import.meta.env.VITE_ADMIN_API_BASE,
  combination: "chain",
  options,
  fetchOptions: {
    mode: "cors",
  },
});
export const aiResearchApiClient = createFetch({
  baseUrl: import.meta.env.VITE_YDC_API_BASE,
  combination: "chain",
  options,
  fetchOptions: {
    mode: "cors",
  },
});
export const unfurlApiClient = createFetch({
  baseUrl: import.meta.env.VITE_INFOKANAL_API_BASE,
  combination: "chain",
  options,
  fetchOptions: {
    mode: "cors",
  },
});

export const intelligenceApiClient = createFetch({
  baseUrl: import.meta.env.VITE_INTELLIGENCE_API_BASE,
  combination: "chain",
  options,
  fetchOptions: {
    mode: "cors",
  },
});
